import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import $api from '../api'
import type { PlaylistBrief, Song } from './playlistSlice'

type SearchState = {
  // индекс (загруженные данные)
  indexLoading: boolean
  indexLoaded: boolean
  indexError: string | null

  playlistsIndex: PlaylistBrief[]
  songsIndex: Song[]

  // текущий запрос
  query: string

  // результаты (уже отфильтрованные)
  loading: boolean
  suggestions: string[]
  playlists: PlaylistBrief[]
  songs: Song[]
}

export const initialState: SearchState = {
  indexLoading: false,
  indexLoaded: false,
  indexError: null,

  playlistsIndex: [],
  songsIndex: [],

  query: '',

  loading: false,
  suggestions: [],
  playlists: [],
  songs: [],
}

function buildSuggestions(q: string) {
  const base = q.trim()
  if (!base) return []
  return [`${base} house`, `${base} lounge`, `${base} funk`]
}

function norm(v?: string) {
  return (v ?? '').toString().toLowerCase().trim()
}

function matchPlaylist(p: PlaylistBrief, q: string) {
  const qq = norm(q)
  if (!qq) return false

  const title = norm(p.title)
  const descEn = norm(p.description?.en)
  const descFr = norm(p.description?.fr)

  return title.includes(qq) || descEn.includes(qq) || descFr.includes(qq)
}

function matchSong(s: Song, q: string) {
  const qq = norm(q)
  if (!qq) return false
  return norm(s.title).includes(qq) || norm(s.artist).includes(qq)
}

function applyFilter(state: SearchState) {
  const q = state.query.trim()

  // “не спамить” на 1 символ
  if (q.length < 2) {
    state.loading = false
    state.suggestions = []
    state.playlists = []
    state.songs = []
    return
  }

  state.loading = false
  state.suggestions = buildSuggestions(q)

  // топ 8 плейлистов
  state.playlists = state.playlistsIndex.filter((p) => matchPlaylist(p, q)).slice(0, 8)

  // топ 6 треков
  state.songs = state.songsIndex.filter((s) => matchSong(s, q)).slice(0, 6)
}

export const loadSearchIndex = createAsyncThunk<
  { playlists: PlaylistBrief[]; songs: Song[] },
  void,
  { rejectValue: string }
>('search/loadIndex', async (_, { rejectWithValue }) => {
  try {
    // 1) плейлисты — точно есть и работает
    const playlistsRes = await $api.post('/playlists/user/public-list', {
      take: 500,
      skip: 0,
    })

    const playlists: PlaylistBrief[] = playlistsRes?.data?.playlists ?? []

    // 2) songs — попробуем (если эндпоинт не существует/не доступен — не валим всё)
    let songs: Song[] = []
    try {
      const songsRes = await $api.post('/songs/user/public-list', {
        take: 2000,
        skip: 0,
      })
      songs = songsRes?.data?.songs ?? songsRes?.data ?? []
      if (!Array.isArray(songs)) songs = []
    } catch {
      songs = []
    }

    return { playlists, songs }
  } catch (err: any) {
    const msg =
      err?.response?.data?.message ?? err?.message ?? 'Failed to load search index'
    return rejectWithValue(typeof msg === 'string' ? msg : JSON.stringify(msg))
  }
})

export const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    setQuery: (state, { payload }: PayloadAction<string>) => {
      state.query = payload
      applyFilter(state)
    },
    clearSearch: (state) => {
      state.query = ''
      state.loading = false
      state.suggestions = []
      state.playlists = []
      state.songs = []
    },
    refilter: (state) => {
      applyFilter(state)
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadSearchIndex.pending, (state) => {
      state.indexLoading = true
      state.indexError = null
    })
    builder.addCase(loadSearchIndex.fulfilled, (state, { payload }) => {
      state.indexLoading = false
      state.indexLoaded = true
      state.indexError = null

      state.playlistsIndex = payload.playlists ?? []
      state.songsIndex = payload.songs ?? []

      // если пользователь уже что-то ввёл — сразу пересчитаем
      applyFilter(state)
    })
    builder.addCase(loadSearchIndex.rejected, (state, action) => {
      state.indexLoading = false
      state.indexLoaded = false
      state.indexError = (action.payload as string) ?? 'Index load failed'
    })
  },
})

export const { setQuery, clearSearch, refilter } = searchSlice.actions
export default searchSlice.reducer
